import type { ContentAnalysis } from "./topicAnalyzer";

export interface TopicAssistantAIResponse {
  analysis: ContentAnalysis;
  suggestions: string[];
}

export interface CitationHint {
  text: string;
  reason: string;
  suggestedSearchQuery?: string;
}

export interface CitationSource {
  title: string;
  authors: string;
  year: string;
  journal: string;
  url: string;
  type: string;
  summary?: string;
}

export interface CitationSuggestion {
  title: string;
  citationText: string;
  contextSentence: string;
}

type AIAssistMode =
  | "topic-insights"
  | "citation-hints"
  | "citation-sources"
  | "citation-suggestions"
  | "ai-usage"
  | "thesis-refine"
  | "command"
  | "chat";

export type AssistantConversationMessage = {
  role: "user" | "assistant";
  content: string;
};

export interface AIAssistCommandResult {
  action: string;
  args?: Record<string, unknown>;
  narration: string;
  confidence: number;
  followUpPrompt?: string;
}

export interface AIAssistChatResponse {
  reply: string;
  suggestions?: string[];
  followUpAction?: string;
}

const FUNCTIONS_ROUTE = "/ai-assist";

export async function fetchTopicAssistantInsights(params: {
  content: string;
  topic: string;
}): Promise<TopicAssistantAIResponse> {
  return callAIAssist<TopicAssistantAIResponse>("topic-insights", params);
}

export async function fetchCitationHints(params: {
  content: string;
  maxHints?: number;
}): Promise<CitationHint[]> {
  return callAIAssist<CitationHint[]>("citation-hints", params);
}

export async function fetchCitationSources(params: {
  hint: string;
  sourceType?: string;
  timeFrame?: string;
}): Promise<CitationSource[]> {
  return callAIAssist<CitationSource[]>("citation-sources", params);
}

export async function fetchCitationSuggestions(params: {
  documentText: string;
  paperFormat: string;
}): Promise<CitationSuggestion[]> {
  return callAIAssist<CitationSuggestion[]>("citation-suggestions", params);
}

export async function estimateAIUsage(params: { content: string }): Promise<{
  aiPercentage: number;
  originalPercentage: number;
  rationale: string;
}> {
  return callAIAssist("ai-usage", params);
}

export async function refineThesisStatement(params: {
  thesis: string;
  topic?: string;
  documentExcerpt?: string;
}): Promise<{
  improvedText: string;
  rationale: string;
  talkingPoints: string[];
  hook?: string;
}> {
  return callAIAssist("thesis-refine", params);
}

export async function requestAIAssistCommand(params: {
  utterance: string;
  context?: Record<string, unknown>;
}): Promise<AIAssistCommandResult> {
  return callAIAssist("command", params);
}

export async function sendAIAssistChat(params: {
  conversation: AssistantConversationMessage[];
  contextSummary?: string;
}): Promise<AIAssistChatResponse> {
  return callAIAssist("chat", params);
}

async function callAIAssist<T = unknown>(mode: AIAssistMode, payload: Record<string, unknown>): Promise<T> {
  const baseUrl = getFunctionsBaseUrl();
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

  if (!baseUrl) {
    throw new Error("Missing Supabase function URL for AI assist");
  }

  const response = await fetch(`${baseUrl}${FUNCTIONS_ROUTE}`, {
    method: "POST",
    headers: buildFunctionHeaders(anonKey),
    body: JSON.stringify({ mode, payload }),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || "AI assist service unavailable");
  }

  const data = await response.json();
  return data?.result as T;
}

function getFunctionsBaseUrl() {
  const override = import.meta.env.VITE_SUPABASE_FUNCTIONS_URL?.replace(/\/$/, "");
  if (override) return override;
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL?.replace(/\/$/, "");
  if (!supabaseUrl) return null;
  return `${supabaseUrl}/functions/v1`;
}

function buildFunctionHeaders(anonKey?: string) {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };

  if (anonKey) {
    headers.apikey = anonKey;
    headers.Authorization = `Bearer ${anonKey}`;
  }

  return headers;
}
